import { z } from 'zod';
import { LOCALES, WEEKDAYS } from '@bulk/types';

export const idSchema = z.uuid();
export const slugSchema = z
  .string()
  .min(2)
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Lowercase letters, digits and dashes only');
export const currencySchema = z.string().length(3).toUpperCase();
/**
 * A published image: where it is, the size it was measured at so a page can reserve its space, and
 * the resized copies for the browser to choose from. Every field but the url is optional in
 * practice — a picture uploaded before any of that was recorded still renders.
 */
export const publicAssetSchema = z.object({
  url: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  srcset: z.string().nullable(),
});
export type PublicAsset = z.infer<typeof publicAssetSchema>;

export const localeSchema = z.enum(LOCALES);
export const weekdaySchema = z.enum(WEEKDAYS);
export const timeHHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM');
export const dateOnlySchema = z.iso.date();
export const dateTimeSchema = z.iso.datetime({ offset: true });
export const timezoneSchema = z.string().min(1).refine(
  (tz) => {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  },
  { message: 'Unknown IANA timezone' },
);

export const moneySchema = z.object({
  amountMinor: z.number().int().nonnegative(),
  currency: currencySchema,
});
export type MoneyInput = z.infer<typeof moneySchema>;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const dateRangeQuerySchema = z.object({
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
});
export type DateRangeQuery = z.infer<typeof dateRangeQuerySchema>;

export const searchQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
});

export const sortOrderSchema = z.enum(['asc', 'desc']);

type WithoutDefault<T> = T extends z.ZodDefault<infer Inner> ? Inner : T;
type WithoutDefaults<S extends z.ZodRawShape> = { [K in keyof S]: WithoutDefault<S[K]> };

/**
 * The patch counterpart of a create schema. `.partial()` alone is not enough: it keeps every
 * field's `.default()`, so parsing `{}` returns the defaults and a PATCH rewrites fields the
 * caller never sent. Dropping the defaults first makes an absent field stay absent; a nested
 * object still fills its own defaults when it is sent, because a patch replaces it wholesale.
 */
export function patchOf<S extends z.ZodRawShape>(schema: z.ZodObject<S>) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const [key, field] of Object.entries(schema.shape as unknown as Record<string, z.ZodTypeAny>)) {
    shape[key] = field instanceof z.ZodDefault ? (field.def.innerType as z.ZodTypeAny) : field;
  }
  return z.object(shape as unknown as WithoutDefaults<S>).partial();
}

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int(),
    page: z.number().int(),
    pageSize: z.number().int(),
  });
}

export const cursorSchema = z.string().trim().min(1).max(200).describe('The nextCursor of the previous page: the rows after it, whatever the page');

/** A long list read page by page or by cursor; `total` is counted only for a request without a cursor. */
export function keysetPageSchema<T extends z.ZodTypeAny>(item: T) {
  return pageSchema(item).extend({
    total: z.number().int().nullable().describe('Counted for a request without a cursor; null when reading on from a cursor'),
    nextCursor: z.string().nullable().describe('Pass as cursor for the next older page; null on the last page'),
  });
}

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});

export const okSchema = z.object({ ok: z.literal(true) });
